import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Pie from '../Charts/Pie'
import FallbackLoadingView from './FallbackLoadingView'
import { landUseLabels, landUseColors } from './insightsSettings'
import { toggleSliceVisibility } from './insightsUtils'


const LandUseView = ({ lbcsData, combinedLbcsData, isLoading }) => {
  const [chart, setChart] = useState(null)
  const [hoveredSlice, setHoveredSlice] = useState(null)
  const [selectedSlice, setSelectedSlice] = useState(null)
  const [visibility, setVisibility] = useState({})

  // Fallback or Loading State
  if(
    isLoading ||
    !lbcsData ||
    lbcsData.length === 0 ||
    !combinedLbcsData ||
    Object.keys(combinedLbcsData)?.length === 0 ||
    !landUseLabels
  ) {
    return (
      <FallbackLoadingView id="insights-land-use" title="Activity Counts" isLoading={isLoading}>
        <p className="small">
          The following chart shows the distribution of Land-Based Classification System (LBCS) activities within your currently selected place.
          <a
            className="margin-left-sm"
            href="https://support.regrid.com/parcel-data/lbcs"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about our Standardized Land Use Codes
            <i className="fas fa-external-link-alt margin-left-sm"></i>
          </a>
          .
        </p>
      </FallbackLoadingView>
    )
  }

  // --- EVENT HANDLERS ---
  const handleMouseEnter = (rawKey) => {
    if(chart) {
      const seriesData = chart.series[0]?.data || []
      const chartSlice = seriesData.find(
        (slice) => slice.options.rawKey === rawKey
      )
      if(chartSlice) {
        chartSlice.setState('hover')
        chart.redraw() // Force a redraw to apply the state change
      }
    }
  }

  const handleMouseLeave = () => {
    if(chart) {
      chart.series[0].data.forEach((slice) => slice.setState('')) // Reset all states
      chart.redraw() // Force redraw of the chart
    }
  }

  const handleSliceHover = (rawKey) => {
    setHoveredSlice(rawKey)
  }

  const selectSingleSlice = (rawKey) => {
    if(chart) {
      const seriesData = chart.series[0]?.data || []
      const chartSlice = seriesData.find(
        (slice) => slice.options.rawKey === rawKey
      )

      // n.b. receiving a `TypeError: chartSlice.setSlice is not a function` error
      // when trying to toggle slice visibility, ...but it doesn't work without this?
      // TODO: investigate click event bubble, button now nested within slice
      if(chartSlice) {
        const isSliced = chartSlice.sliced
        chartSlice.select(null, false) // Toggle select and unselect any previously selected slices
        chartSlice.setSlice(!isSliced) // Toggle slice and keep other slices sliced
      }
    }
  }

  const handleSliceClick = (rawKey) => {
    if(selectedSlice === rawKey) {
      setSelectedSlice(null)
      selectSingleSlice(rawKey) // chart internal state can toggle
    } else {
      setSelectedSlice(rawKey)  // component state
      selectSingleSlice(rawKey) // chart internal state
    }
  }


  const handleToggleVisibility = (rawKey) => {
    toggleSliceVisibility(chart, rawKey, setVisibility)
  }

  const selectedSliceData = combinedLbcsData[selectedSlice]
  const selectedSliceLabel = landUseLabels?.[selectedSlice] ?? selectedSlice
  const selectedSliceColor = landUseColors?.[selectedSlice] ?? 'black'
  const selectedSlicePercent = (
    (selectedSliceData /
      Object.values(combinedLbcsData).reduce((acc, cur) => acc + cur, 0)) *
    100
  ).toFixed(0)
  const selectedSlicePercentDisplay =
    selectedSliceData && selectedSlicePercent === '0'
      ? '<1%'
      : `${selectedSlicePercent}%`


  // --- RENDER ---
  return (
    <div id="insights-land-use" className="flex-col-start">
      <div className="row width-100 sticky-header">
        <div className="col-sm-12">
          <h3>Activity Counts</h3>
          <p className="small">
            The following chart shows the distribution of Land-Based
            Classification System (LBCS) activities within your currently
            selected place.
            <a
              className="margin-left-sm"
              href="https://support.regrid.com/parcel-data/lbcs"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about our Standardized Land Use Codes
              <i className="fas fa-external-link-alt margin-left-sm"></i>
            </a>
            .
          </p>
          <p className="hr"></p>
        </div>
      </div>
      <div className="flex-row-start width-100">
        <div className="row width-100">
          <div className="col-md-12 col-lg-6 col-lg-push-6">
            <div className="land-use-chart">
              <Pie
                title="LBCS Activity Counts"
                data={combinedLbcsData}
                labelsByKey={landUseLabels}
                colorsByKey={landUseColors}
                onSliceHover={handleSliceHover}
                onSliceClick={handleSliceClick}
                setChart={setChart}
              />
            </div>
            {selectedSlice && (
              <div className="land-use-highlight flex-col-start">
                <h4>Selected Activity</h4>
                <div className="flex-row-start">
                  <span className="bold">Name</span>
                  <span className="">
                    {selectedSliceLabel}
                    <i
                      className="fas fa-circle margin-left-md margin-right-md"
                      style={{ color: selectedSliceColor }}
                    ></i>
                  </span>
                </div>
                <div className="flex-row-start">
                  <span className="bold">Count</span>
                  <span className="">{selectedSliceData.toLocaleString()}</span>
                </div>
                {selectedSlicePercentDisplay && (
                  <div className="flex-row-start">
                    <span className="bold">Percentage</span>
                    <span className="">{selectedSlicePercentDisplay}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="col-md-12 col-lg-6 col-lg-pull-6">
            <ul className="land-use-list simple">
              {Object.entries(combinedLbcsData)
                .sort((a, b) => b[1] - a[1]) // Sort by value descending
                .map(([rawKey, count]) => {
                  const isHovered = rawKey === hoveredSlice
                  const isSelected = rawKey === selectedSlice
                  const displayName = landUseLabels?.[rawKey] ?? rawKey
                  const displayColor = landUseColors?.[rawKey] ?? 'black'
                  const isVisible = visibility[rawKey] !== false
                  return (
                    <li
                      key={rawKey}
                      onMouseEnter={() => handleMouseEnter(rawKey)}
                      onMouseLeave={() => handleMouseLeave(rawKey)}
                      className={
                        `flex-row-start` +
                        (isHovered ? ' hovered' : '') +
                        (isSelected ? ' selected' : '')
                      }
                    >
                      <div
                        className="flex-row-start"
                        onClick={(e) => {
                          e.preventDefault() // Prevent chart click event
                          handleSliceClick(rawKey)
                        }}
                      >
                        <span className="land-use-icon">
                          <i
                            className="fas fa-circle margin-left-md margin-right-md"
                            style={{ color: displayColor }}
                          ></i>
                        </span>
                        <span className="land-use-name bold truncate">
                          {displayName}
                        </span>
                        <span className={`land-use-value ${isVisible ? '' : 'strike'}`}>
                          {count.toLocaleString()}
                        </span>
                        <button
                          title="Toggle visibility in chart"
                          className="btn btn-round btn-inline btn-sm land-use-toggle"
                          onClick={(e) => {
                            e.preventDefault() // Prevent list item click event
                            handleToggleVisibility(rawKey)
                          }}
                        >
                          <i className={`fas ${isVisible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                        </button>
                      </div>
                    </li>
                  )
                })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

LandUseView.propTypes = {
  lbcsData:         PropTypes.array,
  combinedLbcsData: PropTypes.object,
  isLoading:        PropTypes.bool.isRequired,
}

export default LandUseView